import i18n from "./i18n";

// 前端注入的“交易复核”菜单入口。
// 后端菜单暂未包含该页面，这里在 formatRoutes 之前把节点插入到
// “销售/订单(Order)”菜单分组下，让动态路由与侧边栏都能自动识别。
// 节点的 path + component 会被 formatRoutes 解析为
// @/views/order/TransactionReview.vue，路由地址 /order/transaction-review。

const NODE_ID = "__tx_review__";

const buildNode = () => ({
  id: NODE_ID,
  name: i18n.t("txReview.menuTitle"),
  icon: "el-icon-money",
  path: "/order",
  component: "TransactionReview"
});

// 是否已注入（防止重复登录/刷新时重复插入）
const alreadyInjected = nodes => {
  if (!Array.isArray(nodes)) {
    return false;
  }
  return nodes.some(node => {
    if (!node) {
      return false;
    }
    if (node.id === NODE_ID) {
      return true;
    }
    return alreadyInjected(node.children);
  });
};

// 查找包含指定叶子节点的父级 children 数组
const findSiblings = (nodes, predicate) => {
  if (!Array.isArray(nodes)) {
    return null;
  }
  for (const node of nodes) {
    if (node && Array.isArray(node.children)) {
      if (node.children.some(child => child && predicate(child))) {
        return node.children;
      }
      const deep = findSiblings(node.children, predicate);
      if (deep) {
        return deep;
      }
    }
  }
  return null;
};

// 隐藏旧「交易(Order)」列表与「退款(OrderRefund)」台账节点——
// 二者均已并入「交易复核」（Refunds 作为其一个分段），侧边栏隐藏、路由保留。
const LEGACY_ORDER_LEAVES = ["Order", "OrderRefund"];
const hideLegacyOrderNodes = nodes => {
  if (!Array.isArray(nodes)) {
    return;
  }
  nodes.forEach(node => {
    if (!node) {
      return;
    }
    if (LEGACY_ORDER_LEAVES.indexOf(node.component) !== -1) {
      node.hideInNav = true;
    }
    hideLegacyOrderNodes(node.children);
  });
};

export const injectTransactionReviewMenu = menuList => {
  if (!Array.isArray(menuList) || menuList.length === 0) {
    return menuList;
  }
  if (alreadyInjected(menuList)) {
    hideLegacyOrderNodes(menuList);
    return menuList;
  }

  const node = buildNode();
  // 优先精确匹配真实交易页面(Order)，其次匹配 order 路径
  const siblings =
    findSiblings(menuList, child => child.component === "Order") ||
    findSiblings(menuList, child => child.path && String(child.path).indexOf("order") !== -1);

  if (siblings) {
    siblings.push(node);
    // 旧「交易(Order)」列表已被「交易复核」取代：侧边栏隐藏，路由保留
    hideLegacyOrderNodes(menuList);
  } else {
    // 兜底：作为顶层分组，保证入口可见可达
    menuList.push({
      id: NODE_ID + "_group",
      name: i18n.t("txReview.menuGroup"),
      icon: "el-icon-shopping-cart-full",
      children: [node]
    });
  }
  return menuList;
};

// =============================================================
// 商品中心（ProductHub）菜单注入：
// 把「我的商品 / 素材库 / 分类 / 标签」四个入口整合为一个「商品中心」，
// 旧节点标记 hideInNav（侧边栏隐藏，但保留在 menuList 中以便
// formatRoutes 仍生成其路由，深链/回退不受影响）。
// =============================================================
const HUB_ID = "__product_hub__";
const HUB_LEGACY = ["Product", "ProductLibrary", "ProductType", "ProductTag"];

const hubInjected = nodes => {
  if (!Array.isArray(nodes)) {
    return false;
  }
  return nodes.some(node => {
    if (!node) {
      return false;
    }
    if (node.id === HUB_ID) {
      return true;
    }
    return hubInjected(node.children);
  });
};

// 标记旧商品叶子节点为导航隐藏；返回是否有命中
const hideLegacyProductNodes = nodes => {
  if (!Array.isArray(nodes)) {
    return false;
  }
  let hit = false;
  nodes.forEach(node => {
    if (!node) {
      return;
    }
    if (node.component && HUB_LEGACY.indexOf(node.component) !== -1) {
      node.hideInNav = true;
      hit = true;
    }
    if (hideLegacyProductNodes(node.children)) {
      hit = true;
    }
  });
  return hit;
};

export const injectProductHubMenu = menuList => {
  if (!Array.isArray(menuList) || menuList.length === 0) {
    return menuList;
  }
  if (hubInjected(menuList)) {
    hideLegacyProductNodes(menuList);
    return menuList;
  }

  const node = {
    id: HUB_ID,
    name: i18n.t("menu.ProductHub"),
    icon: "el-icon-goods",
    path: "/product",
    component: "ProductHub"
  };

  const siblings =
    findSiblings(menuList, child => child.component === "Product") ||
    findSiblings(menuList, child => child.path && String(child.path).indexOf("product") !== -1);

  if (siblings) {
    // 插到第一个旧商品节点的位置，保持组内次序直观
    const anchor = siblings.findIndex(child => child && HUB_LEGACY.indexOf(child.component) !== -1);
    siblings.splice(anchor === -1 ? siblings.length : anchor, 0, node);
    hideLegacyProductNodes(menuList);
  } else {
    // 后端没有商品菜单（无权限用户）：不注入，避免出现越权入口
    return menuList;
  }
  return menuList;
};

// =============================================================
// 数据分析（AnalyticsHub）菜单注入：
// 把「按日期 / 按设备 / 按商品 / 按用户」四个统计入口整合为一个「数据分析」，
// 旧节点标记 hideInNav（侧边栏隐藏，路由保留）。
// =============================================================
const ANALYTICS_ID = "__analytics_hub__";
const ANALYTICS_LEGACY = ["DateProfit", "MachineProfit", "ProductProfit", "UserProfit"];

const analyticsInjected = nodes => {
  if (!Array.isArray(nodes)) {
    return false;
  }
  return nodes.some(node => {
    if (!node) {
      return false;
    }
    if (node.id === ANALYTICS_ID) {
      return true;
    }
    return analyticsInjected(node.children);
  });
};

const hideLegacyAnalyticsNodes = nodes => {
  if (!Array.isArray(nodes)) {
    return false;
  }
  let hit = false;
  nodes.forEach(node => {
    if (!node) {
      return;
    }
    if (node.component && ANALYTICS_LEGACY.indexOf(node.component) !== -1) {
      node.hideInNav = true;
      hit = true;
    }
    if (hideLegacyAnalyticsNodes(node.children)) {
      hit = true;
    }
  });
  return hit;
};

export const injectAnalyticsMenu = menuList => {
  if (!Array.isArray(menuList) || menuList.length === 0) {
    return menuList;
  }
  if (analyticsInjected(menuList)) {
    hideLegacyAnalyticsNodes(menuList);
    return menuList;
  }

  const node = {
    id: ANALYTICS_ID,
    name: i18n.t("menu.Analytics"),
    icon: "el-icon-data-line",
    path: "/statistics",
    component: "AnalyticsHub"
  };

  const siblings =
    findSiblings(menuList, child => child.component && ANALYTICS_LEGACY.indexOf(child.component) !== -1) ||
    findSiblings(menuList, child => child.path && String(child.path).indexOf("statistic") !== -1);

  if (siblings) {
    const anchor = siblings.findIndex(child => child && ANALYTICS_LEGACY.indexOf(child.component) !== -1);
    siblings.splice(anchor === -1 ? siblings.length : anchor, 0, node);
    hideLegacyAnalyticsNodes(menuList);
  } else {
    // 后端没有统计菜单（无权限）：不注入
    return menuList;
  }
  return menuList;
};

// =============================================================
// 钱包模块导航可见性（默认隐藏，管理员开关控制）。
// 钱包相关页面目前未使用：默认从侧边栏隐藏（路由仍保留，管理员启用开关后可见）。
// =============================================================
import { isWalletVisible } from "./featureFlags";

const WALLET_COMPONENTS = ["Topup", "Record", "Account", "Detail", "WithdrawalRecord", "RechargeRecord"];
const isWalletNode = node => {
  if (!node || !node.component) {
    return false;
  }
  const path = String(node.path || "");
  // 钱包页（/mywallet/*）或订单里的充值/提现记录
  if (path.indexOf("mywallet") !== -1 || path.indexOf("wallet") !== -1) {
    return true;
  }
  return node.component === "WithdrawalRecord" || node.component === "RechargeRecord";
};

const markWallet = (nodes, hidden) => {
  if (!Array.isArray(nodes)) {
    return;
  }
  nodes.forEach(node => {
    if (!node) {
      return;
    }
    if (isWalletNode(node) && WALLET_COMPONENTS.indexOf(node.component) !== -1) {
      node.hideInNav = hidden;
    }
    markWallet(node.children, hidden);
  });
};

// =============================================================
// 设备中心（MachinesHub）菜单注入：
// 把「我的设备 / 设备分组 / 设备标签 / 分组转移 / 设备地图×2」六个入口整合为一个「设备」，
// 旧节点标记 hideInNav（侧边栏隐藏，路由保留）。
// =============================================================
const MACHINES_HUB_ID = "__machines_hub__";
// 运营者(/machine/user)下的机器入口统一收进 MachinesHub：不再出现独立「USER MACHINES」分区。
// 注意 MachineGroup 这个组件名在 /machine/admin 与 /machine/user 两处复用，GroupTransfer 组件名也是 MachineGroup，
// 因此 MachineGroup 只按 /machine/user 路径隐藏（见 hideLegacyMachineNodes），保留管理区(/machine/admin)的设备分组。
const MACHINES_LEGACY = ["MachineUser", "MachineGroup", "MachineLabels", "Location", "MachineMaps"];

const machinesInjected = nodes => {
  if (!Array.isArray(nodes)) {
    return false;
  }
  return nodes.some(node => {
    if (!node) {
      return false;
    }
    if (node.id === MACHINES_HUB_ID) {
      return true;
    }
    return machinesInjected(node.children);
  });
};

const hideLegacyMachineNodes = nodes => {
  if (!Array.isArray(nodes)) {
    return false;
  }
  let hit = false;
  nodes.forEach(node => {
    if (!node) {
      return;
    }
    if (node.component && MACHINES_LEGACY.indexOf(node.component) !== -1) {
      // MachineGroup 组件名在 admin/user 两处复用——仅隐藏运营者(/machine/user)下的，保留管理区的设备分组
      const isUserMachinePath = String(node.path || "").indexOf("/machine/user") === 0;
      if (node.component !== "MachineGroup" || isUserMachinePath) {
        node.hideInNav = true;
        hit = true;
      }
    }
    if (hideLegacyMachineNodes(node.children)) {
      hit = true;
    }
  });
  return hit;
};

export const injectMachinesHubMenu = menuList => {
  if (!Array.isArray(menuList) || menuList.length === 0) {
    return menuList;
  }
  if (machinesInjected(menuList)) {
    hideLegacyMachineNodes(menuList);
    return menuList;
  }

  const node = {
    id: MACHINES_HUB_ID,
    name: i18n.t("menu.MachinesHub"),
    icon: "el-icon-cpu",
    path: "/machine/user",
    component: "MachinesHub"
  };

  // 只在「我的设备(MachineUser)」所在的经销商设备分组注入，避免命中后台设备台账
  const siblings = findSiblings(menuList, child => child.component === "MachineUser");

  if (siblings) {
    const anchor = siblings.findIndex(child => child && MACHINES_LEGACY.indexOf(child.component) !== -1);
    siblings.splice(anchor === -1 ? siblings.length : anchor, 0, node);
    hideLegacyMachineNodes(menuList);
  } else {
    return menuList;
  }
  return menuList;
};

// =============================================================
// 广告中心（AdvertsHub）菜单注入：
// 把「广告库(Adverts) / 广告投放(Desktop)」整合为一个「广告」入口，
// 旧节点标记 hideInNav（侧边栏隐藏，路由保留）。这也修复了旧「广告投放」
// 因 path/component 与文件位置不匹配导致的失效导航——统一走可解析的 Hub 路由。
// =============================================================
const ADVERTS_HUB_ID = "__adverts_hub__";
const ADVERTS_LEGACY = ["Adverts", "Desktop"];

// 判定菜单节点是否属于「广告」范畴。用于隐藏所有旧广告入口——包括后端菜单里
// 仍存在、但前端已无对应视图组件的失效链接（如「设备广告 / 标签广告」）。
// 语言无关：同时按 组件名 / 路径 / 显示名（中英）匹配。
const isAdvertNode = node => {
  if (!node) {
    return false;
  }
  const c = String(node.component || "");
  const p = String(node.path || "");
  const n = String(node.name || "");
  if (ADVERTS_LEGACY.indexOf(c) !== -1 || /advert/i.test(c) || /Ads?$/.test(c)) {
    return true;
  }
  if (/adver/i.test(p)) {
    return true;
  }
  if (/广告/.test(n)) {
    return true;
  }
  if (/\bads?\b/i.test(n) && !/^add\b/i.test(n.trim())) {
    return true;
  }
  return false;
};

const advertsInjected = nodes => {
  if (!Array.isArray(nodes)) {
    return false;
  }
  return nodes.some(node => {
    if (!node) {
      return false;
    }
    if (node.id === ADVERTS_HUB_ID) {
      return true;
    }
    return advertsInjected(node.children);
  });
};

// 隐藏所有旧广告「叶子」入口（保留分组容器与注入的 Hub），修复失效链接并统一入口。
const hideLegacyAdvertsNodes = nodes => {
  if (!Array.isArray(nodes)) {
    return false;
  }
  let hit = false;
  nodes.forEach(node => {
    if (!node) {
      return;
    }
    const isLeaf = node.component && (!node.children || node.children.length === 0);
    if (isLeaf && node.id !== ADVERTS_HUB_ID && isAdvertNode(node)) {
      node.hideInNav = true;
      hit = true;
    }
    if (hideLegacyAdvertsNodes(node.children)) {
      hit = true;
    }
  });
  return hit;
};

export const injectAdvertsHubMenu = menuList => {
  if (!Array.isArray(menuList) || menuList.length === 0) {
    return menuList;
  }
  if (advertsInjected(menuList)) {
    hideLegacyAdvertsNodes(menuList);
    return menuList;
  }

  const node = {
    id: ADVERTS_HUB_ID,
    name: i18n.t("menu.AdvertsHub"),
    icon: "el-icon-video-camera",
    path: "/adverts",
    component: "AdvertsHub"
  };

  const siblings =
    findSiblings(menuList, child => child.component === "Adverts") ||
    findSiblings(menuList, child => isAdvertNode(child) && child.id !== ADVERTS_HUB_ID);

  if (siblings) {
    const anchor = siblings.findIndex(child => child && ADVERTS_LEGACY.indexOf(child.component) !== -1);
    siblings.splice(anchor === -1 ? siblings.length : anchor, 0, node);
    hideLegacyAdvertsNodes(menuList);
  } else {
    // 后端没有广告菜单（无权限）：不注入
    return menuList;
  }
  return menuList;
};

export const applyWalletVisibility = menuList => {
  if (!Array.isArray(menuList) || menuList.length === 0) {
    return menuList;
  }
  // 隐藏（除非管理员开启）；路由不受影响，仅侧边栏可见性
  markWallet(menuList, !isWalletVisible());
  return menuList;
};

export default {
  injectTransactionReviewMenu,
  injectProductHubMenu,
  injectAnalyticsMenu,
  injectMachinesHubMenu,
  injectAdvertsHubMenu,
  applyWalletVisibility
};
